import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, AlertCircle, Package } from "lucide-react";
import { TONES } from "@/data/content";
import { Reveal } from "@/components/Reveal";
import { GoldButton, Chip } from "@/components/Section";
import { ServiceVisual } from "@/components/services/ServiceVisual";
import { SERVICE_ICONS } from "@/components/home/ServicesTeaser";

const BACKDROPS = {
  strategy: "mesh-copper",
  exports: "mesh-slate",
  transformation: "mesh-sage",
  "ai-digital": "bg-white",
  "bi-market": "bg-[#F2EEE7]",
  commercial: "bg-white",
};

const Clusters = ({ s, t }) => {
  const [open, setOpen] = useState(0);
  return (
    <div data-testid={`clusters-${s.id}`}>
      <div className="flex flex-wrap gap-2" role="tablist">
        {s.clusters.map((c, i) => (
          <button
            key={c.name}
            role="tab"
            aria-selected={open === i}
            data-testid={`cluster-tab-${s.id}-${i}`}
            onClick={() => setOpen(i)}
            className={`rounded-full px-4 py-2 text-[12px] font-semibold transition-all duration-300 border ${open === i ? "text-white border-transparent shadow-md" : "bg-white/70 text-[#3B4A5E] border-[#0E1B31]/10 hover:border-[#0E1B31]/30"}`}
            style={{ background: open === i ? t.accent : undefined }}
          >
            {c.name}
            <span className="ml-2 font-mono-brand text-[10px] opacity-70">{c.items.length}</span>
          </button>
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={open}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.3 }}
          className="mt-4 flex flex-wrap gap-2"
        >
          {s.clusters[open].items.map((c) => (
            <Chip key={c} tone={t.text} tint="#ffffff" className="border-[#0E1B31]/10 hover:!border-current">
              {c}
            </Chip>
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export const ServiceSection = ({ s, idx }) => {
  const Icon = SERVICE_ICONS[s.icon];
  const t = TONES[s.tone];
  const flip = idx % 2 === 1;

  return (
    <section id={s.id} data-testid={`service-section-${s.id}`} className={`relative scroll-mt-24 overflow-hidden py-20 lg:py-28 ${BACKDROPS[s.id]}`}>
      <div className="absolute inset-x-0 top-0 hairline" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
              <div className="flex items-center gap-4">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl text-white shadow-lg" style={{ background: `linear-gradient(135deg, ${t.accent}, ${t.accent}BB)` }}>
                  <Icon className="h-6 w-6" />
                </span>
                <div>
                  <p className="font-mono-brand text-[11px] tracking-[0.3em] uppercase" style={{ color: t.accent }}>Άξονας {s.num} / 06</p>
                  <p className="text-xs text-[#6B7A8F] mt-0.5">{s.tagline}</p>
                </div>
              </div>
              <h2 className="mt-7 font-display text-3xl sm:text-4xl lg:text-[2.9rem] font-semibold leading-[1.1] text-[#0E1B31]">{s.title}</h2>
              <p className="mt-5 text-base lg:text-lg leading-relaxed text-[#3B4A5E]">{s.desc}</p>

              <div className="mt-6 rounded-[22px] border-l-4 bg-white/70 backdrop-blur p-5" style={{ borderColor: t.accent }}>
                <p className="font-mono-brand text-[10px] tracking-[0.25em] uppercase text-[#6B7A8F]">Γιατί έχει εμπορική σημασία</p>
                <p className="mt-2 text-sm leading-relaxed text-[#0E1B31]">{s.value}</p>
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-[22px] border border-[#0E1B31]/[0.07] bg-white p-5">
                  <p className="flex items-center gap-2 font-mono-brand text-[10px] tracking-[0.25em] uppercase text-[#6B7A8F]">
                    <AlertCircle className="h-3.5 w-3.5" style={{ color: t.accent }} /> Πότε το χρειάζεστε
                  </p>
                  <ul className="mt-3 space-y-2">
                    {s.signals.map((x) => (
                      <li key={x} className="flex items-start gap-2 text-[13px] leading-snug text-[#3B4A5E]">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: t.accent }} />
                        {x}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-[22px] p-5 text-white" style={{ background: `linear-gradient(150deg, #0E1B31, ${t.accent})` }}>
                  <p className="flex items-center gap-2 font-mono-brand text-[10px] tracking-[0.25em] uppercase text-white/70">
                    <Package className="h-3.5 w-3.5 text-[#C5A059]" /> Τι παραλαμβάνετε
                  </p>
                  <ul className="mt-3 space-y-2">
                    {s.receives.map((x) => (
                      <li key={x} className="flex items-start gap-2 text-[13px] leading-snug text-white/90">
                        <Check className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#C5A059]" />
                        {x}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-10">
                <GoldButton to={`/contact?interest=${encodeURIComponent(s.title)}`} testId={`service-cta-${s.id}`}>
                  Συζητήστε τις ανάγκες της επιχείρησής σας
                </GoldButton>
              </div>
            </div>

            <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""} lg:sticky lg:top-28`}>
              <ServiceVisual service={s} />
              <div className="mt-8">
                <p className="font-mono-brand text-[10px] tracking-[0.3em] uppercase text-[#6B7A8F] mb-4">Τι περιλαμβάνει</p>
                <Clusters s={s} t={t} />
              </div>
              <p className="mt-6 text-[13px] leading-relaxed text-[#6B7A8F] italic">{s.note}</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
